import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { v4 } from 'uuid'
import { l } from '@manychat/manyui'

import {
  ConversationEndReason,
  ConversationStatus,
  InstantAnswersPlaygroundInitialState,
  InstantAnswersSocketEvent,
  Message,
} from 'apps/aiAlwaysOnAutomations/store/instantAnswersPlayground/types'
import { AiAutomationPlaygroundApi } from 'shared/api/requests/aiAutomationPlayground'
import { createAsyncAction } from 'shared/lib/redux'
import { accountNotificationsListener } from 'utils/services/notificationsService'

const NAMESPACE = 'instantAnswersPlayground'

const { reducer, actions } = createSlice({
  initialState: InstantAnswersPlaygroundInitialState,
  name: NAMESPACE,
  reducers: {
    sessionReset: () => InstantAnswersPlaygroundInitialState,

    messageAdded: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload)
      state.status = ConversationStatus.ONGOING
    },

    finishReasonSet: (state, action: PayloadAction<ConversationEndReason>) => {
      state.endReason = action.payload
      state.status = ConversationStatus.FINISHED
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startSessionFx.pending, (state) => {
      state.status = ConversationStatus.ONGOING
    })

    builder.addCase(startSessionFx.fulfilled, (state, action) => {
      state.sessionId = action.payload
    })

    builder.addMatcher(
      isAnyOf(startSessionFx.pending.match, sendMessageFx.pending.match),
      (state, action) => {
        state.status = ConversationStatus.ONGOING_WAITING

        state.messages.push({
          type: 'text',
          author: 'user',
          data: {
            text: action.meta.arg.message,
          },
        })
      },
    )
  },
})

const startSessionFx = createAsyncAction(
  `${NAMESPACE}/startSessionFx`,
  async ({ message }: { message: string }) => {
    const sessionId = v4()

    await AiAutomationPlaygroundApi.startSession({
      body: {
        user_message: message,
        session_id: sessionId,
      },
    })

    return sessionId
  },
)

const sendMessageFx = createAsyncAction(
  `${NAMESPACE}/sendMessageFx`,
  async ({ message }: { message: string }, { getState }) => {
    const sessionId = InstantAnswersPlaygroundSelectors.getSessionId(getState()) as string

    await AiAutomationPlaygroundApi.sendMessage({
      body: {
        session_id: sessionId,
        user_message: message,
      },
    })
  },
  {
    condition: (_, { getState }) =>
      InstantAnswersPlaygroundSelectors.getSessionId(getState()) !== null ||
      InstantAnswersPlaygroundSelectors.getIsOngoingConversation(getState()),
  },
)

accountNotificationsListener.on(
  InstantAnswersSocketEvent.MESSAGE_GENERATED,
  (data: { message: { text: string }; session_id: string }, dispatch, getState) => {
    if (
      InstantAnswersPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id
    ) {
      return
    }

    dispatch(
      actions.messageAdded({
        type: 'text',
        author: 'ai',
        data: {
          text: data.message.text,
        },
      }),
    )
  },
)

accountNotificationsListener.on(
  InstantAnswersSocketEvent.SESSION_FINISHED,
  (
    data: {
      session_id: string
      reason: ConversationEndReason.SUCCESS | ConversationEndReason.OPERATOR_REQUESTED
    },
    dispatch,
    getState,
  ) => {
    if (
      InstantAnswersPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id
    ) {
      return
    }

    dispatch(
      actions.messageAdded({
        type: 'system',
        author: 'ai',
        data: {
          text:
            data.reason === ConversationEndReason.SUCCESS
              ? l.translate('Looks like AI’s all done here.')
              : l.translate('AI doesn’t recognize this one!'),
        },
      }),
    )
    dispatch(actions.finishReasonSet(data.reason))
  },
)

accountNotificationsListener.on(
  InstantAnswersSocketEvent.SCENARIO_NOT_RECOGNIZED,
  (data: { session_id: string }, dispatch, getState) => {
    if (
      InstantAnswersPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id
    ) {
      return
    }

    dispatch(
      actions.messageAdded({
        type: 'system',
        author: 'ai',
        data: {
          text: l.translate('AI doesn’t recognize this one!'),
        },
      }),
    )
    dispatch(actions.finishReasonSet(ConversationEndReason.NOT_RECOGNIZED))
  },
)

export const InstantAnswersPlaygroundReducer = reducer

export const InstantAnswersPlaygroundActions = {
  startSessionFx,
  sendMessageFx,

  sessionReset: actions.sessionReset,
}

const getState = (state: RootState) => state.aiAutomation.instantAnswersPlayground

export const InstantAnswersPlaygroundSelectors = {
  getSessionId: (state: RootState) => getState(state).sessionId,
  getMessages: (state: RootState) => getState(state).messages,
  getIsIdleConversation: (state: RootState) => getState(state).status === ConversationStatus.IDLE,
  getIsOngoingConversation: (state: RootState) =>
    getState(state).status === ConversationStatus.ONGOING,
  getIsOngoingWaitingConversation: (state: RootState) =>
    getState(state).status === ConversationStatus.ONGOING_WAITING,
  getIsFinishedConversation: (state: RootState) =>
    getState(state).status === ConversationStatus.FINISHED,
  getConversationEndReason: (state: RootState) => getState(state).endReason,
}
